import React, { useEffect, useState } from "react";
import { Modal, Form, Input, InputNumber, Button, Divider, Upload, message } from "antd";
import { UserOutlined, SaveOutlined, UploadOutlined, LoadingOutlined, CloseOutlined } from "@ant-design/icons";
import AddImage from "../../Global Functions/addImage";
import "./reviewRatingPanelPopup.css";

const ReviewRatingEditModal = ({
    visible,
    onClose,
    reviewRatingData,
    onSave,
}) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [typeDisplay, setTypeDisplay] = useState("Home Page");

    useEffect(() => {
        if (visible && reviewRatingData) {
            const imageFileList = reviewRatingData.image
                ? [{ uid: '-1', name: 'current-image.jpg', status: 'done', url: reviewRatingData.image }]
                : [];

            setFileList(imageFileList);
            setTypeDisplay((reviewRatingData.type || "home_page").replace(/_/g, " "));
            form.setFieldsValue({
                ...reviewRatingData,
                type: reviewRatingData.type || "home_page",
                image: imageFileList,
            });
        } else if (visible) {
            form.resetFields();
            form.setFieldsValue({ type: "home_page" });
            setTypeDisplay("Home Page");
            setFileList([]);
        }
    }, [visible, reviewRatingData, form]);

    const customUploadRequest = async ({ file, onSuccess, onError, onProgress }) => {
        try {
            setUploading(true);
            const imageUrl = await AddImage(file);

            onSuccess({ url: imageUrl }, file);
            message.success("Image uploaded successfully!");

            setFileList(prev => {
                const updated = prev.map(item =>
                    item.uid === file.uid ? { ...item, status: 'done', url: imageUrl } : item
                );
                form.setFieldsValue({ image: updated });
                return updated;
            });
        } catch (error) {
            message.error("Upload failed!");
            onError(error);
        } finally {
            setUploading(false);
        }
    };

    const handleFinish = async (values) => {
        const finalImageUrl = values.image?.[0]?.url || null;
        const formValues = {
            ...values,
            image: finalImageUrl,
            type: values.type || "home_page",
        };
        await onSave(formValues);
    };

    const handleUploadChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const uploadProps = {
        customRequest: customUploadRequest,
        maxCount: 1,
        listType: "picture",
        accept: "image/*",
        showUploadList: true,
    };

    return (
        <Modal
            title={
                <div className="review-rating-edit-modal-header-content">
                    <UserOutlined className="review-rating-edit-modal-header-icon" style={{ color: "white" }} />
                    <span style={{ color: "white" }}>
                        {reviewRatingData?._id ? "EDIT REVIEW" : "ADD REVIEW"}
                    </span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            closeIcon={
                <Button
                    type="text"
                    icon={<CloseOutlined style={{ color: 'white', fontSize: '18px' }} />}
                    onClick={onClose}
                    style={{
                        // borderRadius: '50%',
                        // width: '32px',
                        // height: '32px',
                        marginTop: "0px",
                        marginRight: "47px",
                        padding: "6px",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid white',
                        color: "white"
                    }}
                    className="review-rating-close-btn"
                />
            }
            destroyOnClose
            width={700}
            className="review-rating-edit-modal-override"
            styles={{
                header: {
                    background: "#0D152B",
                    borderRadius: "12px 12px 0 0",
                    padding: "20px 24px",
                    borderBottom: "none",
                },
                body: {
                    padding: "24px",
                    background: "#fafafa",
                },
            }}
        >
            <div className="review-rating-edit-modal-form">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                >
                    <div className="review-rating-edit-section">
                        <h3 className="review-rating-edit-section-header">Basic Information</h3>
                        <div className="review-rating-edit-grid">
                            <Form.Item
                                label="Name"
                                name="name"
                                rules={[{ required: true, message: "Please input name" }]}
                            >
                                <Input
                                    size="large"
                                    placeholder="Enter name"
                                    className="review-rating-edit-input-override"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Profession"
                                name="profession"
                            // rules={[{ required: true, message: "Please input profession" }]}
                            >
                                <Input
                                    size="large"
                                    placeholder="Enter profession"
                                    className="review-rating-edit-input-override"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Type"
                                name="type"
                                initialValue="home_page"
                                rules={[{ required: true, message: "Please enter type" }]}
                                getValueProps={(value) => ({
                                    value: value ? value.replace(/_/g, " ") : "",
                                })}
                                getValueFromEvent={(e) => {
                                    return e.target.value.toLowerCase().replace(/\s+/g, "_");
                                }}
                            >
                                <Input
                                    size="large"
                                    placeholder="e.g. Home Page, Project, Builder..."
                                    className="review-rating-edit-input-override"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Rating"
                                name="rating"
                                rules={[
                                    { required: true, message: "Please input rating" },
                                    { type: 'number', min: 1, max: 5, message: "Rating must be 1-5 only" }
                                ]}
                            >
                                <InputNumber
                                    min={1}
                                    max={5}
                                    step={1}
                                    precision={0}
                                    size="large"
                                    style={{ width: '100%' }}
                                    className="review-rating-edit-input-override"
                                    onKeyDown={(e) => {
                                        const allowedKeys = ['0', '1', '2', '3', '4', '5', 'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                                        if (!allowedKeys.includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    onChange={(value) => {
                                        const clampedValue = Math.max(1, Math.min(5, parseInt(value) || 1));
                                        form.setFieldsValue({ rating: clampedValue });
                                    }}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Image"
                                name="image"
                                valuePropName="fileList"
                                className="review-rating-edit-image-item"
                                getValueFromEvent={(e) => {
                                    if (Array.isArray(e)) {
                                        return e;
                                    }
                                    return e?.fileList || [];
                                }}
                            >
                                <Upload
                                    {...uploadProps}
                                    fileList={fileList}
                                    onChange={handleUploadChange}
                                >
                                    <Button>
                                        {uploading ? <LoadingOutlined /> : <UploadOutlined />}
                                        {uploading ? 'Uploading...' : 'Click to Upload'}
                                    </Button>
                                </Upload>
                            </Form.Item>
                        </div>
                    </div>

                    <Divider className="review-rating-edit-divider" />

                    <div className="review-rating-edit-section">
                        <h3 className="review-rating-edit-section-header">Review Details</h3>
                        <Form.Item
                            label="Review"
                            name="review"
                            rules={[{ required: true, message: "Please input review" }]}
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder="Enter detailed review"
                                className="review-rating-edit-input-override"
                                style={{ resize: 'vertical' }}
                            />
                        </Form.Item>
                    </div>

                    <Divider className="review-rating-edit-divider" />

                    <Form.Item style={{ marginBottom: 0 }}>
                        <div className="review-rating-edit-actions">
                            <Button
                                size="large"
                                onClick={onClose}
                                className="review-rating-edit-btn review-rating-edit-btn-cancel review-rating-edit-close-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                htmlType="submit"
                                size="large"
                                icon={<SaveOutlined />}
                                loading={uploading}
                                className="review-rating-edit-btn review-rating-edit-btn-primary review-rating-edit-btn-override"
                            >
                                <span style={{ color: "white" }}>
                                    {reviewRatingData?._id ? "Update Review" : "Add Review"}
                                </span>
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default ReviewRatingEditModal;
